import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MediaPreviewPanel, type MediaPreviewPanelHandle, Sheet, SheetContent } from "@nexus/ui"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store"
import type { MyBrandCreativeCardModel } from "../../lib/drilldown-card.types"
import {
  drilldownWindowSpanDays,
  type DrilldownPageFiltersInput,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import { mapMyBrandDrawerModel } from "../../lib/my-brand-drawer-model"
import { mapMyBrandDrawerNarrative } from "../../lib/my-brand-drawer-narrative"
import { parseMyBrandDrawerRows } from "../../lib/parse-my-brand-drawer-data"
import { shouldKeepDrilldownDrawerOpenOnOutsideInteraction } from "../../lib/drilldown-drawer-utils"
import { useMyBrandDrawerDataSection } from "../../hooks/useMyBrandDrawerData"
import { useMyBrandDrawerPerformanceDataSection } from "../../hooks/useMyBrandDrawerPerformanceData"
import { useMyBrandDrawerFatigueDataSection } from "../../hooks/useMyBrandDrawerFatigueData"
import type { FatigueGranularity } from "../../lib/my-brand-drawer-fatigue-catalog"
import type { PerformanceKpiId } from "../../lib/my-brand-drawer-performance-catalog"
import { DrilldownMyBrandCreativeInfoTab } from "./DrilldownMyBrandCreativeInfoTab"
import { DrilldownMyBrandCreativePerformanceTab } from "./DrilldownMyBrandCreativePerformanceTab"
import { DrilldownMyBrandCreativeFatigueTab } from "./DrilldownMyBrandCreativeFatigueTab"

type DrawerTab = "info" | "performance" | "fatigue"

export interface DrilldownMyBrandCreativeDrawerProps {
  creative: MyBrandCreativeCardModel | null
  open: boolean
  onOpenChange: (open: boolean) => void
  myBrandFilters: MyBrandFiltersInput
  drilldownFilters: DrilldownPageFiltersInput
}

export function DrilldownMyBrandCreativeDrawer({
  creative,
  open,
  onOpenChange,
  myBrandFilters,
  drilldownFilters,
}: DrilldownMyBrandCreativeDrawerProps) {
  const { t } = useTranslation()
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const northStar = useDashboardFiltersStore((s) => s.northStar)
  const [activeTab, setActiveTab] = useState<DrawerTab>("info")
  const [fatigueSelectedMetrics, setFatigueSelectedMetrics] = useState<PerformanceKpiId[]>(["spend"])
  const [fatigueGranularity, setFatigueGranularity] = useState<FatigueGranularity>("weekly")
  const [showPreviousPeriod, setShowPreviousPeriod] = useState(true)
  const mediaPreviewRef = useRef<MediaPreviewPanelHandle>(null)

  const creativeKey = `${creative?.hash ?? ""}|${creative?.adId ?? ""}`
  useEffect(() => {
    setFatigueSelectedMetrics(["spend"])
    setFatigueGranularity("weekly")
    setShowPreviousPeriod(true)
  }, [creativeKey])

  const drawerData = useMyBrandDrawerDataSection(creative, open, myBrandFilters, drilldownFilters)
  const performanceData = useMyBrandDrawerPerformanceDataSection(
    creative,
    open,
    activeTab,
    myBrandFilters,
    drilldownFilters,
  )
  const fatigueData = useMyBrandDrawerFatigueDataSection(
    creative,
    open,
    activeTab,
    myBrandFilters,
    drilldownFilters,
    fatigueSelectedMetrics,
    fatigueGranularity,
  )

  const fatigueSpanDays = useMemo(
    () => drilldownWindowSpanDays(drilldownFilters, myBrandFilters.dateRange),
    [drilldownFilters, myBrandFilters.dateRange],
  )

  const performanceRow = useMemo(() => {
    return performanceData.section?.rows[0] ?? null
  }, [performanceData.section?.rows])

  const tenants = useAuth((s) => s.tenants)
  const activeTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === activeTenantId) ?? null,
    [tenants, activeTenantId],
  )

  const tenantLogoUrl = activeTenant?.logoUrl ?? null
  const tenantName = activeTenant?.name ?? ""

  const emptyValue = t("drilldown.myBrandDrawer.emptyValue", "—")
  const comingSoonLabel = t("drilldown.myBrandDrawer.comingSoon", "Coming soon")
  const noneLabel = t("drilldown.myBrandDrawer.none", "None")

  const parsed = useMemo(() => {
    return drawerData.section?.rows.length ? parseMyBrandDrawerRows(drawerData.section.rows) : null
  }, [drawerData.section?.rows])

  const narrative = useMemo(
    () => mapMyBrandDrawerNarrative(parsed, emptyValue),
    [parsed, emptyValue],
  )

  const brandName = tenantName

  const model = useMemo(() => {
    if (!creative) return null
    return mapMyBrandDrawerModel(
      creative,
      drawerData.section,
      parsed,
      northStar,
      emptyValue,
      comingSoonLabel,
      noneLabel,
      brandName || emptyValue,
      tenantLogoUrl,
    )
  }, [
    creative,
    drawerData.section,
    parsed,
    northStar,
    emptyValue,
    comingSoonLabel,
    noneLabel,
    brandName,
    tenantLogoUrl,
  ])

  const handleSequenceSeek = useCallback((seconds: number) => {
    mediaPreviewRef.current?.seekTo(seconds)
  }, [])

  const handleTabSelect = useCallback((tab: DrawerTab) => {
    setActiveTab(tab)
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setActiveTab("info")
      }
      onOpenChange(next)
    },
    [onOpenChange],
  )

  const tabs: { id: DrawerTab; label: string }[] = useMemo(
    () => [
      { id: "info", label: t("drilldown.myBrandDrawer.infoTab", "Info") },
      { id: "performance", label: t("drilldown.myBrandDrawer.performanceTab", "Performance") },
      { id: "fatigue", label: t("drilldown.myBrandDrawer.fatigueTab", "Fatigue") },
    ],
    [t],
  )

  return (
    <Sheet open={open} onOpenChange={handleOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        className="flex w-full flex-col overflow-hidden bg-card p-0 sm:max-w-[1080px]"
        onPointerDownOutside={(e) => {
          if (shouldKeepDrilldownDrawerOpenOnOutsideInteraction(e.target)) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if (shouldKeepDrilldownDrawerOpenOnOutsideInteraction(e.target)) e.preventDefault()
        }}
      >
        {model && creative ? (
          <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
            <div className="flex min-h-0 w-full shrink-0 flex-col border-border bg-muted/40 sm:w-[44%] sm:border-r">
              <div className="border-b border-border px-6 py-4 sm:hidden">
                <DrawerTabList tabs={tabs} activeTab={activeTab} onSelect={handleTabSelect} />
              </div>
              <MediaPreviewPanel
                ref={mediaPreviewRef}
                key={model.id}
                contentKey={model.id}
                src={model.assetUrl || undefined}
                thumbnailSrc={model.thumbnailUrl}
                type={model.isVideo ? "video" : "image"}
                alt={model.brandName}
                externalUrl={model.adLibUrl}
                className="min-h-[320px] flex-1"
              />
            </div>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
              <div className="hidden border-b border-border px-6 py-4 sm:block">
                <DrawerTabList tabs={tabs} activeTab={activeTab} onSelect={handleTabSelect} />
              </div>
              {activeTab === "info" ? (
                <DrilldownMyBrandCreativeInfoTab
                  model={model}
                  narrative={narrative}
                  isVideo={model.isVideo}
                  isLoading={drawerData.isLoading}
                  isError={drawerData.isError}
                  onSequenceSeek={handleSequenceSeek}
                />
              ) : activeTab === "performance" ? (
                <DrilldownMyBrandCreativePerformanceTab
                  performanceRow={performanceRow}
                  isLoading={performanceData.isLoading}
                  isError={performanceData.isError}
                />
              ) : activeTab === "fatigue" ? (
                <DrilldownMyBrandCreativeFatigueTab
                  seriesRows={fatigueData.section?.rows ?? null}
                  isLoading={fatigueData.isLoading}
                  isError={fatigueData.isError}
                  spanDays={fatigueSpanDays}
                  selectedMetrics={fatigueSelectedMetrics}
                  onSelectedMetricsChange={setFatigueSelectedMetrics}
                  granularity={fatigueGranularity}
                  onGranularityChange={setFatigueGranularity}
                  showPreviousPeriod={showPreviousPeriod}
                  onShowPreviousPeriodChange={setShowPreviousPeriod}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

function DrawerTabList({
  tabs,
  activeTab,
  onSelect,
}: {
  tabs: { id: DrawerTab; label: string }[]
  activeTab: DrawerTab
  onSelect: (tab: DrawerTab) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onSelect(tab.id)}
          className={
            tab.id === activeTab
              ? "inline-flex rounded-lg bg-primary/10 px-3 py-1.5 text-[13px] font-medium text-primary"
              : "inline-flex rounded-lg px-3 py-1.5 text-[13px] font-medium text-muted-foreground hover:bg-muted/60"
          }
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}
